import type {
  AskReimagineHistoryMessage,
  AskReimagineSuggestionType,
  AskReimagineUnitSession,
} from "../types/axlPilotContext";

const KEY_PREFIX = "askReimagine_session_";

function unitSuffix(capabilityId: number, subCapabilityId: number | null): string {
  return subCapabilityId == null ? `${capabilityId}_direct` : `${capabilityId}_${subCapabilityId}`;
}

export function getAskReimagineSessionKey(
  userId: number | string,
  projectId: number | string,
  capabilityId: number,
  subCapabilityId: number | null
): string {
  return `${KEY_PREFIX}${userId}_${projectId}_${unitSuffix(capabilityId, subCapabilityId)}`;
}

export function loadAskReimagineUnitSession(
  userId: number | string,
  projectId: number | string,
  capabilityId: number,
  subCapabilityId: number | null
): AskReimagineUnitSession | null {
  try {
    const raw = sessionStorage.getItem(
      getAskReimagineSessionKey(userId, projectId, capabilityId, subCapabilityId)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AskReimagineUnitSession;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      started: Boolean(parsed.started),
      history: Array.isArray(parsed.history) ? parsed.history : [],
      suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions : [],
      suggestion_type:
        parsed.suggestion_type === "answers" ? "answers" : "next_steps",
    };
  } catch {
    return null;
  }
}

export function saveAskReimagineUnitSession(
  userId: number | string,
  projectId: number | string,
  capabilityId: number,
  subCapabilityId: number | null,
  session: AskReimagineUnitSession
): void {
  try {
    sessionStorage.setItem(
      getAskReimagineSessionKey(userId, projectId, capabilityId, subCapabilityId),
      JSON.stringify(session)
    );
  } catch {
    // ignore quota errors
  }
}

export function clearAllAskReimagineSessions(): void {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function createEmptyAskReimagineSession(): AskReimagineUnitSession {
  return {
    started: false,
    history: [],
    suggestions: [],
    suggestion_type: "next_steps",
  };
}

export function applyAssistantTurn(
  session: AskReimagineUnitSession,
  userMessage: string | null,
  assistantReply: string,
  suggestions: string[],
  suggestionType: AskReimagineSuggestionType
): AskReimagineUnitSession {
  const history: AskReimagineHistoryMessage[] = [...session.history];
  if (userMessage) {
    history.push({ role: "user", content: userMessage });
  }
  history.push({ role: "assistant", content: assistantReply });

  return {
    started: true,
    history,
    suggestions: suggestions ?? [],
    suggestion_type: suggestionType === "answers" ? "answers" : "next_steps",
  };
}
